import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { CustomFormComponent } from '@bo-client/activation-management/custom-form.component';
import {
  ActionWithMoreInfoModel,
  DataForCustomFormModel
} from '@bo-client/activation-management/shared/models/actionWithMoreInfo.model';
import { CommissioningRequestDtoModel } from '@bo-client/activation-management/shared/models/commissioningRequestDto';
import { BehaviorSubject, combineLatest, map, Observable, of } from 'rxjs';
import { PriceCalculationUtil } from '@bo-client/shared/services/price-calc.util';
import { ErpOrder } from '@bo-client/shared/erp-integration/erp-order-list/erp.order';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { HttpHapService } from '@bo-client/core/http/http-hap.service';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  shareReplay,
  switchMap,
  tap
} from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { FormControl } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';

@Component({
  selector: 'app-create-erp-order',
  templateUrl: './create-erp-order.component.html',
  styleUrls: ['./create-erp-order.component.scss']
})
export class CreateErpOrderComponent
  implements
    CustomFormComponent<
      CommissioningRequestDtoModel,
      {
        additionalData: string;
        comment: string;
      }
    >,
    OnInit
{
  @Input()
  data: DataForCustomFormModel<CommissioningRequestDtoModel>;

  @Output()
  onConfirm: EventEmitter<{ additionalData: string; comment: string }> = new EventEmitter();

  // In Ihrer Komponente
  public searchControl = new FormControl();

  initialBPOptions$: Observable<
    { customerNo: string; name: string; zipCode: string; city: string; street: string; isDeleted: boolean }[]
  >;
  order: ErpOrder = new ErpOrder();

  displayedColumns: string[] = ['name', 'externalId', 'remove'];
  dataSource = new MatTableDataSource();
  @ViewChild(MatTable) table: MatTable<any>;
  actions: ActionWithMoreInfoModel[] = [];
  public lineItemOptions$: Observable<any[]>;

  public isLoadingCustomers$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public manualSelection: boolean = false;

  public selectedBusinessPartner: {
    customerNo?: string;
    city: string;
    name: string;
    zipCode: string;
    isDeleted?: boolean;
    street: string;
  } = null;

  public bpOptions$: Observable<
    { customerNo: string; name: string; zipCode: string; city: string; street: string; isDeleted: boolean }[]
  >;

  constructor(
    private dataShareService: DataShareService,
    private httpHapService: HttpHapService,
    private toastService: ToastrService,
    public dialogRef: MatDialogRef<CreateErpOrderComponent>
  ) {
    this.order = {
      externalBusinessPartnerId: '',
      externalId: '',
      connectionObjectReadable: '',
      mwstGrouped: [],
      totalPriceBrutto: 0,
      totalPriceNetto: 0,
      positions: []
    };
  }

  ngOnInit() {
    this.bpOptions$ = this.searchControl.valueChanges.pipe(
      filter((x) => x && x.length > 4), // nur eingaben mit mehr als 5 stellen (um die Anzahl an ergebnissen einzugrenzen)
      debounceTime(300), // Wartezeit von 300ms nach jeder Eingabe
      distinctUntilChanged(), // Ignoriert, wenn sich der nächste Suchtext nicht geändert hat
      switchMap((value) => {
        const customerNoObs = this.findCustomers({
          customerNo: value,
          city: '',
          street: '',
          firstName: '',
          lastName: '',
          zipCode: ''
        }).pipe(
          catchError((error) => {
            // Fehlerbehandlung
            return of([]); // Leeres Observable zurückgeben, um den Stream fortzusetzen
          })
        );

        const lastNameObs = this.findCustomers({
          customerNo: '',
          city: '',
          street: '',
          firstName: '',
          lastName: value,
          zipCode: ''
        }).pipe(
          catchError((error) => {
            // Fehlerbehandlung
            return of([]); // Leeres Observable zurückgeben, um den Stream fortzusetzen
          })
        );

        return combineLatest([customerNoObs, lastNameObs]).pipe(
          map(([customerNoOptions, lastNameOptions]) => {
            // Hier können Sie die Ergebnisse aus beiden Observables weiterverarbeiten
            // z.B. in einem Array zusammenführen oder filtern
            return [...customerNoOptions, ...lastNameOptions];
          })
        );
      }) // Suchanfrage
    );

    // maybe filter this thing ?
    this.lineItemOptions$ = this.httpHapService
      .getTyped<{
        content: {
          id: number;
          name: string;
          amount: number;
          tax: number;
          amountBrutto: number;
          externalId: string;
          quantity: number;
          customAttributes: {
            [key: string]: any;
          };
        }[];
      }>('/erp-integration/line-items?lookup=')
      .pipe(
        map((response) => response.content),
        map((x) => {
          return x.filter((y) => {
            // filter by geschäftsbereich passt zur aktuellen sparte
            if (y.customAttributes.GSBER) {
              // custom mapping evi = TODO move this somewhere else
              switch (y.customAttributes.GSBER) {
                case '10':
                  // nur bei Strom
                  return this.data.model.area.name === 'POWER';
                case '20':
                  // nur bei Strom
                  return this.data.model.area.name === 'GAS';
                case '30':
                  // nur bei Strom
                  return this.data.model.area.name === 'WATER';
                case '60':
                  // nur bei Strom
                  return this.data.model.area.name === 'POWER';
                default:
                  return true;
              }
            } else {
              return true;
            }
          });
        })
      );

    /**
     * Bei Laden der Seite wollen wir einmal mit dem übergeben User gucken gehen, ob wir ein Match finden.
     */
    this.initialBPOptions$ = of({
      city: this.data.model.customer.city,
      customerNo: '',
      firstName: '',
      lastName: this.data.model.customer.lastName,
      zipCode: this.data.model.customer.zipCode,
      street: this.data.model.customer.street
    }).pipe(
      tap((x) => this.isLoadingCustomers$.next(true)),
      switchMap((x) => this.findCustomers(x)),
      tap((x) => {
        if (x.length == 0) {
          this.selectedBusinessPartner = {
            customerNo: null,
            name: this.data.model.customer.firstName + ' ' + this.data.model.customer.lastName,
            zipCode: this.data.model.customer.zipCode,
            city: this.data.model.customer.city,
            street: this.data.model.customer.street + this.data.model.customer.streetNo
          };
        }
        this.isLoadingCustomers$.next(false);
      }),
      shareReplay(1)
    );

    this.updateTotal();
    this.dataSource = new MatTableDataSource(this.order.positions);
  }

  private findCustomers(x: {
    city: string;
    customerNo: string;
    firstName: string;
    lastName: string;
    zipCode: string;
    street: string;
  }) {
    return this.httpHapService
      .postTyped<{
        content: {
          customerNo: string;
          name: string;
          zipCode: string;
          city: string;
          street: string;
          isDeleted: boolean;
        }[];
      }>(x, '/erp-integration/business-partners/find')
      .pipe(
        map((response) => {
          return response.content;
        })
      );
  }

  useCustomerAsNewBusinessPartner() {
    const customer = this.data.model.customer || {};
    const displayName = [customer.firstName, customer.lastName].filter(Boolean).join(' ').trim();

    this.selectedBusinessPartner = {
      customerNo: null,
      name: customer.company || displayName || 'Neuer Geschäftspartner',
      zipCode: customer.zipCode || '',
      city: customer.city || '',
      street: [customer.street, customer.streetNo].filter(Boolean).join(' ').trim()
    };
  }

  createOrder() {
    if (this.selectedBusinessPartner == null) {
      // TODO: Fehlermeldung
      return;
    }

    if (this.selectedBusinessPartner.customerNo == null) {
      // das ist für neue der Fall => Neuen Kunden zuerst anlegen

      const customer = this.data.model.customer || {};
      const contactData = {
        ...customer,
        contactType: this.deriveContactType(customer)
      };

      this.httpHapService
        .post({ contactData }, '/erp-integration/business-partners')
        .pipe(
          map((response) => {
            return response.erpCustomerId;
          }),
          tap((x) => {
            this.toastService.success('Kunden erfolgreich angelegt. - ' + x);
            this.order.externalBusinessPartnerId = x;
            this.selectedBusinessPartner.customerNo = x;
            this.createOrderInternal();
          })
        )
        .subscribe();
    } else {
      this.order.externalBusinessPartnerId = this.selectedBusinessPartner.customerNo;
      this.createOrderInternal();
    }
  }

  private deriveContactType(customer: any): 'PERSON' | 'COMPANY' {
    if (customer?.contactType === 'PERSON' || customer?.contactType === 'COMPANY') {
      return customer.contactType;
    }

    if (customer?.company || customer?.companyAddition) {
      return 'COMPANY';
    }

    if (customer?.salutation || customer?.firstName || customer?.lastName) {
      return 'PERSON';
    }

    return 'PERSON';
  }

  createOrderInternal() {
    this.order.connectionObjectReadable = this.data.model.connectionObject.readable;
    this.order.commissioningDateReadable = this.data.model.supplyAppointment
      ? new Date(this.data.model.supplyAppointment).toLocaleDateString()
      : '';

    this.httpHapService
      .postTyped<{
        billingOrderId?: string;
        messages: string[];
      }>(
        {
          referenceId: this.data.model.id,
          ...this.order
        },
        '/erp-integration/orders'
      )
      .pipe(
        map((response) => {
          return response;
        }),
        tap((x) => {
          if (x.billingOrderId) {
            this.toastService.success('Auftrag erfolgreich angelegt.');
            this.onConfirm.emit({ additionalData: '', comment: '' });
          } else {
            this.toastService.warning(
              'Bei der Auftragsanlage ist ein Fehler aufgetreten. ',
              x.messages.join(' - \n')
            );
          }
        })
      )
      .subscribe();
  }

  selectBusinessPartner(option: any) {
    this.selectedBusinessPartner = option;
    return false;
  }

  deletePosition(index: number): void {
    // Entfernt das Element an der Position 'index' aus dem 'positions'-Array
    this.order.positions.splice(index, 1);
    this.updateTotal();
    this.table.renderRows();
  }

  addPosition(position: {
    id: null;
    name: '';
    amount: 0;
    tax: 19;
    amountBrutto: 0;
    externalId: '';
    additionalText: '';
    quantity: 1;
  }) {
    console.log('adding position');
    this.order.positions.push(position);
    this.updateTotal();
    this.table.renderRows();
  }

  updateTotal() {
    this.order = {
      ...this.order,
      ...PriceCalculationUtil.calculatePrices(this.order.positions)
    };
  }
}
